import { format, isSameDay, parse } from 'date-fns';
import { useState } from 'react';
import type { DateRange, Matcher } from 'react-day-picker';
import { cn } from '../lib/utils';
import { Button } from '../primitives/button';
import { Calendar } from '../primitives/calendar';
import {
  DATE_RANGE_PRESET_LABELS,
  type DateRangePreset,
  type DateRangeValue,
  resolveDateRange,
} from './model';

/**
 * The panel body every popover-shaped date-range control shares: a macro list
 * down the left, a two-month range calendar on the right.
 *
 * Extracted from `DateRangePopover` so the FilterBar chip editor renders the
 * identical thing rather than a second, thinner editor — "pick a macro here,
 * type two dates over there" was exactly the divergence this replaces.
 * Presentation only: it owns no open/closed state, and reports a preset pick
 * back through `onPresetPicked` so the caller decides whether to close.
 */
const ISO = 'yyyy-MM-dd';
const toDate = (iso?: string) => (iso ? parse(iso, ISO, new Date()) : undefined);
const toIso = (d: Date) => format(d, ISO);
/** The two days as [earlier, later]. */
const ordered = (a: Date, b: Date): [Date, Date] => (b < a ? [b, a] : [a, b]);

export interface DateRangePanelProps {
  value: DateRangeValue;
  /** Presets to offer in the sidebar (order preserved). */
  presets: readonly DateRangePreset[];
  /**
   * The span the calendar can reach, as `YYYY-MM-DD` bounds. Supplying BOTH
   * turns the month caption into month and year dropdowns.
   *
   * Off by default, because a calendar that opens on the window it already has
   * and steps a month at a time is right for a range near today — which is
   * nearly every range. It stops being right where the control is the ONLY way
   * to name a date: reaching a month four years back is then fifty clicks on
   * an arrow, and a caption that jumps is the difference between "possible"
   * and "possible in practice".
   */
  earliest?: string;
  latest?: string;
  onChange: (next: DateRangeValue) => void;
  /**
   * Called after a preset is applied. `false` for Custom — the user still has
   * to pick dates, so the panel must stay open. Callers use it to close.
   */
  onPresetPicked?: (preset: DateRangePreset, shouldClose: boolean) => void;
  /**
   * The start picked by a first click, as `YYYY-MM-DD` — or `undefined` once
   * the range is finished or dropped. It never reaches `onChange` (see
   * below), so a caller that prints the window it is running uses this to
   * say that a pick is under way instead of contradicting the calendar.
   */
  onPendingChange?: (from: string | undefined) => void;
  className?: string;
}

export function DateRangePanel({
  value,
  presets,
  earliest,
  latest,
  onChange,
  onPresetPicked,
  onPendingChange,
  className,
}: DateRangePanelProps) {
  const jumpable = earliest !== undefined && latest !== undefined;
  // Every day picked on the calendar is a custom range, so a control that does
  // not offer Custom does not draw one: the macro list is the whole editor,
  // and the caller's own summary says which dates the macro covers.
  const offersCustom = presets.includes('custom');
  // A custom range ARRIVING at a control that does not offer one — a shared
  // link, a definition saved before the control narrowed. Named in the list,
  // selected and inert, so the reader can see what is running (the caller's
  // footer has its dates) and knows a macro will replace it; without it the
  // list highlights nothing and the window looks like no choice at all.
  const strandedCustom = !offersCustom && value.preset === 'custom';
  const resolved = resolveDateRange(value);
  const isAllTime = value.preset === 'all_time';

  // The calendar highlights the resolved window (presets included) or the
  // in-progress custom pick. "All time" leaves it unselected.
  const selected: DateRange | undefined = isAllTime
    ? undefined
    : resolved
      ? { from: toDate(resolved.from), to: toDate(resolved.to) }
      : value.preset === 'custom'
        ? { from: toDate(value.from), to: toDate(value.to) }
        : undefined;

  // Click, click: the first click starts a NEW range, the second ends it.
  //
  // rdp's own range mode never starts over from a complete range — a click
  // moves whichever end suits (past the start sets the end, before it the
  // start, ON an end collapses to that day). So there was no way to move the
  // start later, and "click the day I want to start on" did something else.
  //
  // The half-picked start stays HERE rather than going up through `onChange`:
  // a custom value with no `to` resolves to no window at all, so a caller
  // applying every change would briefly run unfiltered between the two
  // clicks. Nothing leaves the panel until the range is whole, and closing it
  // (or picking a preset) mid-pick simply drops the start.
  const [anchor, setAnchorState] = useState<Date>();
  const setAnchor = (next: Date | undefined) => {
    setAnchorState(next);
    onPendingChange?.(next ? toIso(next) : undefined);
  };
  // A value arriving from outside — a saved report loading, a reset, a drill —
  // replaces a half-picked start, or the next click would pair the new value's
  // replacement with a start picked against the old one.
  const [seen, setSeen] = useState(value);
  if (seen.preset !== value.preset || seen.from !== value.from || seen.to !== value.to) {
    setSeen(value);
    setAnchorState(undefined);
  }
  const shown: DateRange | undefined = anchor ? { from: anchor, to: undefined } : selected;

  // While a start is picked, tint the days between it and the pointer — the
  // range the second click would make. Not the start itself: it is already
  // drawn as selected, and the tint would wash its dark fill out.
  // Keyboard focus previews too, so arrowing across the grid shows the band.
  const [hovered, setHovered] = useState<Date>();
  let previewModifiers: Record<string, Matcher> | undefined;
  if (anchor && hovered) {
    const [lo, hi] = ordered(anchor, hovered);
    previewModifiers = { preview: (d: Date) => d >= lo && d <= hi && !isSameDay(d, anchor) };
  }

  const pickPreset = (p: DateRangePreset) => {
    setAnchor(undefined);
    if (p === 'custom') {
      // Seed Custom with the current window so the calendar doesn't blank out.
      onChange({
        preset: 'custom',
        from: resolved?.from ?? value.from,
        to: resolved?.to ?? value.to,
      });
      onPresetPicked?.(p, false);
      return;
    }
    onChange({ preset: p });
    onPresetPicked?.(p, true);
  };

  // rdp's proposed range is ignored — only the day clicked matters here.
  const pickDay = (_proposed: DateRange | undefined, day: Date) => {
    if (!anchor) {
      setAnchor(day);
      return;
    }
    const [from, to] = ordered(anchor, day);
    setAnchor(undefined);
    setHovered(undefined);
    // Picking days on the calendar is by definition no longer a named macro.
    onChange({ preset: 'custom', from: toIso(from), to: toIso(to) });
  };

  return (
    <div className={cn('flex', className)}>
      <div className={cn('w-40 space-y-1 p-2', offersCustom && 'border-r')}>
        {presets.map((p) => (
          <Button
            key={p}
            type="button"
            variant={value.preset === p ? 'secondary' : 'ghost'}
            size="sm"
            className="w-full justify-start font-normal"
            onClick={() => pickPreset(p)}
          >
            {DATE_RANGE_PRESET_LABELS[p]}
          </Button>
        ))}
        {strandedCustom && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="w-full justify-start font-normal"
            disabled
            title="Not offered here. Pick a period above to replace it."
          >
            {DATE_RANGE_PRESET_LABELS.custom}
          </Button>
        )}
      </div>
      {offersCustom && (
        <Calendar
          // The calendar's prev/next arrows are absolutely positioned, and the
          // shadcn mirror leaves its root static — so they anchored to the
          // popover and the ‹ landed over the preset rail instead of beside the
          // first month. Lifted with z-10 as well: now that they sit inside the
          // caption row, the full-width caption would otherwise swallow clicks.
          className="relative [&_nav>button]:z-10"
          mode="range"
          numberOfMonths={2}
          showOutsideDays={false}
          defaultMonth={selected?.from}
          selected={shown}
          onSelect={pickDay}
          onDayMouseEnter={(day) => setHovered(day)}
          onDayMouseLeave={() => setHovered(undefined)}
          onDayFocus={(day) => setHovered(day)}
          onDayBlur={() => setHovered(undefined)}
          modifiers={previewModifiers}
          modifiersClassNames={{ preview: 'bg-primary/10 dark:bg-primary/5' }}
          {...(jumpable
            ? {
                captionLayout: 'dropdown' as const,
                startMonth: toDate(earliest),
                endMonth: toDate(latest),
                // The dropdown caption's own slots, which the shadcn mirror does
                // not map — it has to stay a copy of the app's file, and the
                // app's calendars are all label captions.
                //
                // rdp draws each dropdown TWICE over: a native `<select>` for
                // the behaviour, and a span showing the current value for the
                // look. With no rule pinning the select on top of the span, both
                // are simply visible and the caption reads "August August ▸". So
                // the select is stretched over the span and made transparent —
                // the span is what is seen, the select is what is clicked. The
                // label goes to `flex` in the same breath, or the chevron the
                // span carries wraps onto a line of its own.
                classNames: {
                  dropdowns: 'flex items-center gap-2',
                  dropdown_root: 'relative inline-flex items-center',
                  dropdown: 'absolute inset-0 h-full w-full cursor-pointer opacity-0',
                  // The chevron is rotated because the shadcn mirror's `Chevron`
                  // only distinguishes left from right, and rdp asks it for a
                  // DOWN one here — a sideways arrow on something that opens
                  // downwards reads as "go forward a month", which is the
                  // control immediately beside it.
                  caption_label: 'flex items-center gap-0.5 text-sm font-medium [&>svg]:rotate-90',
                },
              }
            : {})}
        />
      )}
      {/* Announces the one state the grid cannot: a start is set and the
          second click is still owed. */}
      <span role="status" className="sr-only">
        {anchor ? `Start ${format(anchor, 'PPP')}. Choose the end date.` : ''}
      </span>
    </div>
  );
}
